import { Mail, Save, Send } from '@tamagui/lucide-icons';
import { Button } from '@unistocks/components/button/button';
import { Form } from '@unistocks/components/form/form';
import { Input } from '@unistocks/components/input/input';
import { Column } from '@unistocks/components/layout/flex';
import { Page } from '@unistocks/components/page/page';
import { QueryState } from '@unistocks/components/query-state/query-state';
import { Heading } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import { EmailSettingsDto } from '@unistocks/proxy/axios-client';
import {
  emailSettingsGetQueryKey,
  useEmailSettingsGetQuery,
  useEmailSettingsSendTestEmailMutation,
  useEmailSettingsUpdateMutation
} from '@unistocks/proxy/axios-client/EmailSettingsQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import React, { useState } from 'react';
import { Separator } from 'tamagui';
import { z } from 'zod';

const emailSettingsSchema = z.object({
  smtpHost: z.string().nullish(),
  smtpPort: z.number().min(1).max(65535),
  smtpUserName: z.string().nullish(),
  smtpPassword: z.string().nullish(),
  smtpDomain: z.string().nullish(),
  defaultFromAddress: z.email(),
  defaultFromDisplayName: z.string().min(1)
});

export const EmailSettings: React.FC = () => {
  const [testEmail, setTestEmail] = useState('');

  const { data: settings } = useEmailSettingsGetQuery();

  const { mutateAsync: updateSettings } = useEmailSettingsUpdateMutation({
    onSuccess: () => {
      toast.success(t('AbpUi::SavedSuccessfully'));
    }
  });

  const { mutateAsync: sendTestEmail, isPending: isSending } =
    useEmailSettingsSendTestEmailMutation({
      onSuccess: () => {
        toast.success(t('AbpSettingManagement::TestEmailSentSuccessfully'));
      }
    });

  const handleSubmit = (data: EmailSettingsDto) =>
    updateSettings({
      ...settings,
      ...data,
      smtpEnableSsl: settings?.smtpEnableSsl ?? false,
      smtpUseDefaultCredentials: settings?.smtpUseDefaultCredentials ?? false
    } as any);

  const handleSendTestEmail = () => {
    if (!testEmail) return;
    sendTestEmail({
      senderEmailAddress: settings?.defaultFromAddress ?? '',
      targetEmailAddress: testEmail,
      subject: 'Test Email',
      body: 'This is a test email from the admin panel.'
    });
  };

  return (
    <QueryState queryKeys={[emailSettingsGetQueryKey()]}>
      <Column key="email-settings" gap={'$3'}>
        <Page.Content>
          <Heading size={'$6'} mb={'$3'}>
            {t('AbpSettingManagement::Menu:Emailing')}
          </Heading>

          <Form
            schema={emailSettingsSchema}
            initialValues={settings}
            triggerText={t('AbpUi::Save')}
            triggerIcon={Save}
            onSubmit={handleSubmit}
          >
            <Form.Input
              name="defaultFromAddress"
              label={t('AbpSettingManagement::DefaultFromAddress')}
              iconAfter={<Mail />}
            />
            <Form.Input
              name="defaultFromDisplayName"
              label={t('AbpSettingManagement::DefaultFromDisplayName')}
            />
            <Form.Input name="smtpHost" label={t('AbpSettingManagement::SmtpHost')} />
            <Form.Input
              name="smtpPort"
              label={t('AbpSettingManagement::SmtpPort')}
              keyboardType="numeric"
            />
            <Form.Input name="smtpUserName" label={t('AbpSettingManagement::SmtpUserName')} />
            <Form.Input
              name="smtpPassword"
              label={t('AbpSettingManagement::SmtpPassword')}
              type={'password'}
            />
            <Form.Input name="smtpDomain" label={t('AbpSettingManagement::SmtpDomain')} />
          </Form>
        </Page.Content>

        <Page.Content>
          <Heading size={'$6'} mb={'$3'}>
            {t('AbpSettingManagement::SendTestEmail')}
          </Heading>

          <Separator mb={'$3'} />

          <Input
            placeholder={t('AbpIdentity::EmailAddress')}
            value={testEmail}
            onChangeText={setTestEmail}
          />

          <Button
            mt={'$3'}
            $md={{ alignSelf: 'flex-end' }}
            Icon={Send}
            loading={isSending}
            disabled={!testEmail}
            onPress={handleSendTestEmail}
          >
            {t('AbpSettingManagement::SendTestEmail')}
          </Button>
        </Page.Content>
      </Column>
    </QueryState>
  );
};

EmailSettings.displayName = 'EmailSettings';
